import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiBox,
  FiSave,
  FiTrendingDown,
  FiTrendingUp,
  FiX,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";
import { config } from "../../../../../../utils/apiUrl";
import useSwal from "../../../../../../hooks/useSwal";
import "./StockManagement.css";

/* =========================================================
   INITIAL FORM
========================================================= */

const INITIAL_FORM = {
  productId: "",

  movementType:
    "stock_in",

  quantity: "",

  referenceType:
    "manual",

  referenceId: "",

  referenceNumber: "",

  purchasePrice: "",

  supplierName: "",

  notes: "",
};


/* =========================================================
   MANUAL STOCK MOVEMENT TYPES

   IMPORTANT:

   Do NOT expose:

   opening_stock
   sale
   sale_cancelled

   opening_stock:
   automatically created during product creation.

   sale:
   should be created automatically by
   invoice/order completion.

   sale_cancelled:
   should be created automatically when
   invoice/order stock is reversed.

   This prevents stock/accounting mismatches.
========================================================= */

const MOVEMENT_TYPES = [
  {
    value:
      "stock_in",

    label:
      "Stock In",

    direction:
      "in",
  },

  {
    value:
      "stock_out",

    label:
      "Stock Out",

    direction:
      "out",
  },

  {
    value:
      "customer_return",

    label:
      "Customer Return",

    direction:
      "in",
  },

  {
    value:
      "supplier_return",

    label:
      "Supplier Return",

    direction:
      "out",
  },

  {
    value:
      "damaged",

    label:
      "Damaged",

    direction:
      "out",
  },

  {
    value:
      "lost",

    label:
      "Lost",

    direction:
      "out",
  },

  {
    value:
      "adjustment_in",

    label:
      "Adjustment In",

    direction:
      "in",
  },

  {
    value:
      "adjustment_out",

    label:
      "Adjustment Out",

    direction:
      "out",
  },
];


/* =========================================================
   REFERENCE TYPES

   Matches stockMovement.model.js
========================================================= */

const REFERENCE_TYPES = [
  {
    value:
      "manual",

    label:
      "Manual",
  },

  {
    value:
      "purchase",

    label:
      "Purchase",
  },

  {
    value:
      "order",

    label:
      "Order",
  },

  {
    value:
      "return",

    label:
      "Return",
  },

  {
    value:
      "product",

    label:
      "Product",
  },

  {
    value:
      "other",

    label:
      "Other",
  },
];


/* =========================================================
   FORMAT CURRENCY
========================================================= */

const formatCurrency = (
  value
) => {
  return new Intl.NumberFormat(
    "en-IN",
    {
      style:
        "currency",

      currency:
        "INR",

      maximumFractionDigits:
        2,
    }
  ).format(
    Number(
      value ||
      0
    )
  );
};


/* =========================================================
   FORMAT NUMBER
========================================================= */

const formatNumber = (
  value
) => {
  return new Intl.NumberFormat(
    "en-IN"
  ).format(
    Number(
      value ||
      0
    )
  );
};


/* =========================================================
   VALID OBJECT ID
========================================================= */

const isValidObjectId = (
  value
) => {
  if (!value) {
    return true;
  }

  return /^[a-f\d]{24}$/i.test(
    String(value)
  );
};


/* =========================================================
   COMPONENT
========================================================= */

const StockAdjustmentDialog = ({
  open,

  product,

  products = [],

  onClose,

  onSuccess,
}) => {
  const api =
    useMemo(
      () =>
        new API(),
      []
    );

  const swal =
    useSwal();


  /* =======================================================
     STATE
  ======================================================= */

  const [
    form,
    setForm,
  ] =
    useState({
      ...INITIAL_FORM,
    });


  const [
    errors,
    setErrors,
  ] =
    useState({});


  const [
    loading,
    setLoading,
  ] =
    useState(false);


  /* =======================================================
     RESET FORM WHEN OPENING
  ======================================================= */

  useEffect(
    () => {
      if (!open) {
        return;
      }


      setForm({
        ...INITIAL_FORM,

        productId:
          product?._id ||
          "",

        purchasePrice:
          product
            ?.purchasePrice ??
          "",

        supplierName:
          product
            ?.supplier
            ?.name ||
          "",
      });


      setErrors(
        {}
      );


      setLoading(
        false
      );

    },
    [
      open,
      product,
    ]
  );


  /* =======================================================
     DO NOT RENDER
  ======================================================= */

  if (!open) {
    return null;
  }


  /* =======================================================
     SELECTED PRODUCT
  ======================================================= */

  const selectedProduct =
    products.find(
      (
        item
      ) =>
        String(
          item?._id
        ) ===
        String(
          form.productId
        )
    ) ||
    (
      String(
        product?._id ||
        ""
      ) ===
      String(
        form.productId ||
        ""
      )
        ? product
        : null
    ) ||
    null;


  /* =======================================================
     STOCK INFO
  ======================================================= */

  const currentStock =
    Math.max(
      Number(
        selectedProduct
          ?.stockQty ||
        0
      ),

      0
    );


  const reservedStock =
    Math.max(
      Number(
        selectedProduct
          ?.reservedQty ||
        0
      ),

      0
    );


  const availableStock =
    Math.max(
      currentStock -
        reservedStock,

      0
    );


  /* =======================================================
     MOVEMENT
  ======================================================= */

  const selectedMovement =
    MOVEMENT_TYPES.find(
      (
        item
      ) =>
        item.value ===
        form.movementType
    ) ||
    MOVEMENT_TYPES[0];


  const isStockIn =
    selectedMovement
      ?.direction ===
    "in";


  const quantity =
    Number(
      form.quantity ||
      0
    );


  const calculatedStock =
    Number.isFinite(
      quantity
    )
      ? (
          isStockIn
            ? currentStock +
              quantity
            : currentStock -
              quantity
        )
      : currentStock;


  const calculatedAvailable =
    Math.max(
      calculatedStock -
        reservedStock,

      0
    );


  /* =======================================================
     FIELD CHANGE
  ======================================================= */

  const handleChange =
    (
      event
    ) => {
      const {
        name,
        value,
      } =
        event.target;


      setForm(
        (
          previous
        ) => ({
          ...previous,

          [name]:
            value,
        })
      );


      if (
        errors[name]
      ) {
        setErrors(
          (
            previous
          ) => ({
            ...previous,

            [name]:
              "",
          })
        );
      }
    };


  /* =======================================================
     PRODUCT CHANGE
  ======================================================= */

  const handleProductChange =
    (
      event
    ) => {
      const productId =
        event.target
          .value;


      const nextProduct =
        products.find(
          (
            item
          ) =>
            String(
              item?._id
            ) ===
            String(
              productId
            )
        );


      setForm(
        (
          previous
        ) => ({
          ...previous,

          productId,

          quantity:
            "",

          purchasePrice:
            nextProduct
              ?.purchasePrice ??
            "",

          supplierName:
            nextProduct
              ?.supplier
              ?.name ||
            "",
        })
      );


      setErrors(
        (
          previous
        ) => ({
          ...previous,

          productId:
            "",

          quantity:
            "",
        })
      );
    };


  /* =======================================================
     MOVEMENT TYPE CHANGE
  ======================================================= */

  const handleMovementChange =
    (
      event
    ) => {
      const movementType =
        event.target
          .value;


      let referenceType =
        form.referenceType;


      /*
       * Provide sensible defaults.
       */

      if (
        movementType ===
        "stock_in"
      ) {
        referenceType =
          "purchase";
      }


      if (
        movementType ===
          "customer_return" ||
        movementType ===
          "supplier_return"
      ) {
        referenceType =
          "return";
      }


      if (
        [
          "stock_out",
          "damaged",
          "lost",
          "adjustment_in",
          "adjustment_out",
        ].includes(
          movementType
        )
      ) {
        referenceType =
          "manual";
      }


      setForm(
        (
          previous
        ) => ({
          ...previous,

          movementType,

          referenceType,

          /*
           * Purchase-specific fields only
           * remain for Stock In.
           */

          purchasePrice:
            movementType ===
            "stock_in"
              ? previous
                  .purchasePrice
              : "",

          supplierName:
            movementType ===
            "stock_in"
              ? previous
                  .supplierName
              : "",
        })
      );


      setErrors(
        (
          previous
        ) => ({
          ...previous,

          movementType:
            "",

          quantity:
            "",
        })
      );
    };


  /* =======================================================
     VALIDATION
  ======================================================= */

  const validateForm =
    () => {
      const validationErrors =
        {};


      /* ===================================================
         PRODUCT
      =================================================== */

      if (
        !form.productId
      ) {
        validationErrors.productId =
          "Please select a product.";
      }


      if (
        form.productId &&
        !selectedProduct
      ) {
        validationErrors.productId =
          "Selected product is not available.";
      }


      /* ===================================================
         MOVEMENT TYPE
      =================================================== */

      if (
        !MOVEMENT_TYPES.some(
          (
            item
          ) =>
            item.value ===
            form.movementType
        )
      ) {
        validationErrors.movementType =
          "Please select a valid movement type.";
      }


      /* ===================================================
         QUANTITY
      =================================================== */

      const normalizedQuantity =
        Number(
          form.quantity
        );


      if (
        !Number.isInteger(
          normalizedQuantity
        ) ||
        normalizedQuantity <=
          0
      ) {
        validationErrors.quantity =
          "Quantity must be a positive whole number.";
      }


      /* ===================================================
         STOCK OUT SAFETY
      =================================================== */

      if (
        !isStockIn &&
        Number.isInteger(
          normalizedQuantity
        ) &&
        normalizedQuantity >
          currentStock
      ) {
        validationErrors.quantity =
          `Only ${formatNumber(
            currentStock
          )} item(s) exist in physical stock.`;
      }


      /* ===================================================
         RESERVED STOCK SAFETY

         Example:

         current = 10
         reserved = 8
         available = 2

         operator may manually remove at most 2.
      =================================================== */

      if (
        !isStockIn &&
        Number.isInteger(
          normalizedQuantity
        ) &&
        normalizedQuantity >
          availableStock
      ) {
        validationErrors.quantity =
          `Only ${formatNumber(
            availableStock
          )} unreserved item(s) can be removed. ${formatNumber(
            reservedStock
          )} item(s) are reserved.`;
      }


      /* ===================================================
         REFERENCE TYPE
      =================================================== */

      if (
        !REFERENCE_TYPES.some(
          (
            item
          ) =>
            item.value ===
            form.referenceType
        )
      ) {
        validationErrors.referenceType =
          "Please select a valid reference type.";
      }


      /* ===================================================
         REFERENCE ID
      =================================================== */

      if (
        form.referenceId &&
        !isValidObjectId(
          form.referenceId
        )
      ) {
        validationErrors.referenceId =
          "Reference ID must be a valid MongoDB ID.";
      }


      /* ===================================================
         PURCHASE PRICE
      =================================================== */

      if (
        form.purchasePrice !==
          "" &&
        (
          Number.isNaN(
            Number(
              form.purchasePrice
            )
          ) ||
          Number(
            form.purchasePrice
          ) < 0
        )
      ) {
        validationErrors.purchasePrice =
          "Purchase price cannot be negative.";
      }


      /* ===================================================
         NOTES
      =================================================== */

      if (
        String(
          form.notes ||
          ""
        ).length >
        1000
      ) {
        validationErrors.notes =
          "Notes cannot exceed 1000 characters.";
      }


      setErrors(
        validationErrors
      );


      return (
        Object.keys(
          validationErrors
        ).length ===
        0
      );
    };


  /* =======================================================
     SUBMIT
  ======================================================= */

  const handleSubmit =
    async (
      event
    ) => {
      event.preventDefault();


      if (
        loading
      ) {
        return;
      }


      if (
        !validateForm()
      ) {
        return;
      }


      try {
        setLoading(
          true
        );


        const payload = {
          productId:
            form.productId,

          movementType:
            form.movementType,

          quantity:
            Number(
              form.quantity
            ),

          referenceType:
            form.referenceType,

          referenceId:
            String(
              form.referenceId ||
              ""
            ).trim(),

          referenceNumber:
            String(
              form.referenceNumber ||
              ""
            ).trim(),

          /*
           * Purchase information only applies
           * to incoming purchase stock.
           */

          purchasePrice:
            form.movementType ===
              "stock_in"
              ? (
                  form.purchasePrice ===
                    ""
                    ? 0
                    : Number(
                        form.purchasePrice
                      )
                )
              : 0,

          supplierName:
            form.movementType ===
              "stock_in"
              ? String(
                  form.supplierName ||
                  ""
                ).trim()
              : "",

          notes:
            String(
              form.notes ||
              ""
            ).trim(),
        };


        const response =
          await api.post(
            config
              .adjustInventoryStock,

            payload
          );


        if (
          response?.status !==
            "success" &&
          response?.success !==
            true
        ) {
          throw new Error(
            response?.message ||
              "Unable to adjust stock."
          );
        }


        await swal.success({
          icon:
            "success",

          title:
            isStockIn
              ? "Stock Added"
              : "Stock Deducted",

          text:
            response?.message ||
            "Stock updated successfully.",

          timer:
            1400,

          showConfirmButton:
            false,
        });


        if (
          typeof onSuccess ===
          "function"
        ) {
          onSuccess(
            response?.data ||
            null
          );
        }

      } catch (
        requestError
      ) {
        console.error(
          "Adjust stock error:",
          requestError
        );


        await swal.error({
          icon:
            "error",

          title:
            "Unable to Adjust Stock",

          text:
            requestError
              ?.response
              ?.data
              ?.message ||
            requestError
              ?.message ||
            "Unable to adjust stock.",
        });

      } finally {
        setLoading(
          false
        );
      }
    };


  /* =======================================================
     CLOSE
  ======================================================= */

  const handleClose =
    () => {
      if (
        loading
      ) {
        return;
      }


      onClose?.();
    };


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div
      className="inventory-stock-dialog-overlay"
    >
      <button
        type="button"
        className="inventory-stock-dialog-backdrop"
        onClick={
          handleClose
        }
        aria-label="Close stock adjustment"
      />


      <div
        className="inventory-stock-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Adjust stock"
      >
        {/* =================================================
            HEADER
        ================================================= */}

        <div
          className="inventory-stock-dialog-header"
        >
          <div>
            <span>
              STOCK MANAGEMENT
            </span>


            <h2>
              Adjust Stock
            </h2>
          </div>


          <button
            type="button"
            className="inventory-stock-dialog-close"
            onClick={
              handleClose
            }
            disabled={
              loading
            }
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>


        {/* =================================================
            FORM
        ================================================= */}

        <form
          onSubmit={
            handleSubmit
          }
        >
          <div
            className="inventory-stock-dialog-body"
          >
            {/* =============================================
                PRODUCT SUMMARY
            ============================================= */}

            <div
              className="inventory-stock-adjustment-summary"
            >
              <div
                className="inventory-stock-adjustment-product-icon"
              >
                <FiBox />
              </div>


              <div>
                <span>
                  Selected Product
                </span>


                <strong>
                  {selectedProduct
                    ?.name ||
                    "No product selected"}
                </strong>


                <small>
                  {selectedProduct
                    ?.sku ||
                    "-"}
                </small>
              </div>
            </div>


            {/* =============================================
                STOCK SUMMARY
            ============================================= */}

            <div
              className="inventory-stock-current-grid"
            >
              <div>
                <span>
                  Current Stock
                </span>


                <strong>
                  {formatNumber(
                    currentStock
                  )}
                </strong>
              </div>


              <div>
                <span>
                  Reserved
                </span>


                <strong>
                  {formatNumber(
                    reservedStock
                  )}
                </strong>
              </div>


              <div>
                <span>
                  Available
                </span>


                <strong>
                  {formatNumber(
                    availableStock
                  )}
                </strong>
              </div>


              <div>
                <span>
                  New Stock
                </span>


                <strong
                  className={
                    calculatedStock <
                    reservedStock
                      ? "danger"
                      : ""
                  }
                >
                  {Number.isFinite(
                    calculatedStock
                  )
                    ? formatNumber(
                        calculatedStock
                      )
                    : formatNumber(
                        currentStock
                      )}
                </strong>
              </div>
            </div>


            {/* =============================================
                FORM GRID
            ============================================= */}

            <div
              className="inventory-stock-form-grid"
            >
              {/* ===========================================
                  PRODUCT
              =========================================== */}

              <div
                className="inventory-stock-form-field inventory-stock-form-field-full"
              >
                <label>
                  Product *
                </label>


                <select
                  name="productId"
                  value={
                    form.productId
                  }
                  onChange={
                    handleProductChange
                  }
                  disabled={
                    loading
                  }
                >
                  <option
                    value=""
                  >
                    Select Product
                  </option>


                  {products.map(
                    (
                      item
                    ) => (
                      <option
                        key={
                          item._id
                        }
                        value={
                          item._id
                        }
                      >
                        {item.name}

                        {item.sku
                          ? ` (${item.sku})`
                          : ""}

                        {" — Stock: "}

                        {formatNumber(
                          item.stockQty ||
                          0
                        )}
                      </option>
                    )
                  )}


                  {product?._id &&
                  !products.some(
                    (
                      item
                    ) =>
                      String(
                        item?._id
                      ) ===
                      String(
                        product._id
                      )
                  ) ? (
                    <option
                      value={
                        product._id
                      }
                    >
                      {product.name}

                      {product.sku
                        ? ` (${product.sku})`
                        : ""}

                      {" — Stock: "}

                      {formatNumber(
                        product.stockQty ||
                        0
                      )}
                    </option>
                  ) : null}
                </select>


                {errors.productId ? (
                  <small>
                    {errors.productId}
                  </small>
                ) : null}
              </div>


              {/* ===========================================
                  MOVEMENT
              =========================================== */}

              <div
                className="inventory-stock-form-field"
              >
                <label>
                  Movement Type *
                </label>


                <select
                  name="movementType"
                  value={
                    form.movementType
                  }
                  onChange={
                    handleMovementChange
                  }
                  disabled={
                    loading
                  }
                >
                  {MOVEMENT_TYPES.map(
                    (
                      movementType
                    ) => (
                      <option
                        key={
                          movementType
                            .value
                        }
                        value={
                          movementType
                            .value
                        }
                      >
                        {
                          movementType
                            .label
                        }
                      </option>
                    )
                  )}
                </select>


                {errors.movementType ? (
                  <small>
                    {
                      errors
                        .movementType
                    }
                  </small>
                ) : null}
              </div>


              {/* ===========================================
                  QUANTITY
              =========================================== */}

              <div
                className="inventory-stock-form-field"
              >
                <label>
                  Quantity *
                </label>


                <input
                  type="number"
                  name="quantity"
                  min="1"
                  step="1"
                  value={
                    form.quantity
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Enter quantity"
                  disabled={
                    loading
                  }
                />


                {errors.quantity ? (
                  <small>
                    {errors.quantity}
                  </small>
                ) : (
                  <span
                    className="inventory-stock-field-help"
                  >
                    {isStockIn
                      ? "Quantity will be added to physical stock."
                      : `Maximum removable: ${formatNumber(
                          availableStock
                        )}`}
                  </span>
                )}
              </div>


              {/* ===========================================
                  REFERENCE TYPE
              =========================================== */}

              <div
                className="inventory-stock-form-field"
              >
                <label>
                  Reference Type
                </label>


                <select
                  name="referenceType"
                  value={
                    form.referenceType
                  }
                  onChange={
                    handleChange
                  }
                  disabled={
                    loading
                  }
                >
                  {REFERENCE_TYPES.map(
                    (
                      referenceType
                    ) => (
                      <option
                        key={
                          referenceType
                            .value
                        }
                        value={
                          referenceType
                            .value
                        }
                      >
                        {
                          referenceType
                            .label
                        }
                      </option>
                    )
                  )}
                </select>


                {errors.referenceType ? (
                  <small>
                    {
                      errors
                        .referenceType
                    }
                  </small>
                ) : null}
              </div>


              {/* ===========================================
                  REFERENCE NUMBER
              =========================================== */}

              <div
                className="inventory-stock-form-field"
              >
                <label>
                  Reference Number
                </label>


                <input
                  type="text"
                  name="referenceNumber"
                  value={
                    form.referenceNumber
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="INV-1001 / PO-1001"
                  disabled={
                    loading
                  }
                />
              </div>


              {/* ===========================================
                  REFERENCE ID
              =========================================== */}

              <div
                className="inventory-stock-form-field"
              >
                <label>
                  Reference ID
                </label>


                <input
                  type="text"
                  name="referenceId"
                  value={
                    form.referenceId
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="MongoDB reference ID"
                  disabled={
                    loading
                  }
                />


                {errors.referenceId ? (
                  <small>
                    {errors.referenceId}
                  </small>
                ) : null}
              </div>


              {/* ===========================================
                  STOCK-IN PURCHASE PRICE
              =========================================== */}

              {form.movementType ===
              "stock_in" ? (
                <>
                  <div
                    className="inventory-stock-form-field"
                  >
                    <label>
                      Purchase Price
                    </label>


                    <input
                      type="number"
                      name="purchasePrice"
                      min="0"
                      step="0.01"
                      value={
                        form.purchasePrice
                      }
                      onChange={
                        handleChange
                      }
                      placeholder="0"
                      disabled={
                        loading
                      }
                    />


                    {errors.purchasePrice ? (
                      <small>
                        {
                          errors
                            .purchasePrice
                        }
                      </small>
                    ) : (
                      <span
                        className="inventory-stock-field-help"
                      >
                        Current cost:{" "}
                        {formatCurrency(
                          selectedProduct
                            ?.purchasePrice
                        )}
                      </span>
                    )}
                  </div>


                  <div
                    className="inventory-stock-form-field"
                  >
                    <label>
                      Supplier Name
                    </label>


                    <input
                      type="text"
                      name="supplierName"
                      value={
                        form.supplierName
                      }
                      onChange={
                        handleChange
                      }
                      placeholder="Supplier name"
                      disabled={
                        loading
                      }
                    />
                  </div>
                </>
              ) : null}


              {/* ===========================================
                  NOTES
              =========================================== */}

              <div
                className="inventory-stock-form-field inventory-stock-form-field-full"
              >
                <label>
                  Notes
                </label>


                <textarea
                  name="notes"
                  rows={
                    4
                  }
                  value={
                    form.notes
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="Reason for stock adjustment..."
                  disabled={
                    loading
                  }
                  maxLength={
                    1000
                  }
                />


                <div
                  className="inventory-stock-notes-meta"
                >
                  {errors.notes ? (
                    <small>
                      {
                        errors
                          .notes
                      }
                    </small>
                  ) : (
                    <span />
                  )}


                  <span>
                    {String(
                      form.notes ||
                      ""
                    ).length}
                    /1000
                  </span>
                </div>
              </div>
            </div>


            {/* =============================================
                DIRECTION PREVIEW
            ============================================= */}

            <div
              className={`inventory-stock-direction-preview ${
                isStockIn
                  ? "stock-in"
                  : "stock-out"
              }`}
            >
              {isStockIn ? (
                <FiTrendingUp />
              ) : (
                <FiTrendingDown />
              )}


              <div>
                <strong>
                  {isStockIn
                    ? "Stock will increase"
                    : "Stock will decrease"}
                </strong>


                <span>
                  {formatNumber(
                    currentStock
                  )}

                  {" → "}

                  {formatNumber(
                    calculatedStock
                  )}

                  {" units"}

                  {!isStockIn ? (
                    <>
                      {" • Available after: "}

                      {formatNumber(
                        calculatedAvailable
                      )}
                    </>
                  ) : null}
                </span>
              </div>
            </div>
          </div>


          {/* =================================================
              FOOTER
          ================================================= */}

          <div
            className="inventory-stock-dialog-footer"
          >
            <button
              type="button"
              className="inventory-stock-dialog-cancel"
              onClick={
                handleClose
              }
              disabled={
                loading
              }
            >
              Cancel
            </button>


            <button
              type="submit"
              className={`inventory-stock-dialog-save ${
                isStockIn
                  ? "stock-in"
                  : "stock-out"
              }`}
              disabled={
                loading ||
                !selectedProduct
              }
            >
              <FiSave />


              {loading
                ? "Updating..."
                : isStockIn
                  ? "Add Stock"
                  : "Deduct Stock"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


export default StockAdjustmentDialog;